// src/components/InvestmentCard.js - VERSION ULTRA COMPACTE ET COMPLÈTE

import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, ProgressBar, Badge } from 'react-bootstrap'; 
import { FaCopy, FaCheckCircle, FaCodeBranch, FaClock } from 'react-icons/fa'; // Ajout de FaClock
import { useNavigate } from 'react-router-dom'; 
// NOTE: Assurez-vous d'importer correctement ces fichiers.
import { investmentSimulations } from '../data/simulations'; 
import ReferralChainModal from './ReferralChainModal'; 
import './InvestmentCard.css'; // Assurez-vous que ce fichier CSS n'ajoute pas d'espacement excessif

// Helper pour formater l'argent (Extrait pour la propreté)
const formatCurrency = (amount) => {
    return amount ? parseFloat(amount).toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' }) : '0 FCFA';
};

// --------------------------------------------------------------------------------------------------
// 🎯 FONCTIONS DE SIMULATION (Laissé inchangé, elles fonctionnent)
// --------------------------------------------------------------------------------------------------

const getRandomSimulation = (simulations) => {
    if (!simulations || simulations.length === 0) {
        return { 
            type: 'Opération', 
            company: 'Donnée Manquante', 
            justification: 'Veuillez vérifier le fichier simulations.js' 
        };
    }
    const randomIndex = Math.floor(Math.random() * simulations.length);
    return simulations[randomIndex];
};

const generateRandomSimulations = () => {
    const numSimulations = Math.floor(Math.random() * 3) + 3; 
    const simulations = [];
    
    for (let i = 0; i < numSimulations; i++) {
        simulations.push(getRandomSimulation(investmentSimulations));
    }
    return simulations;
};

// --------------------------------------------------------------------------------------------------
// 🎯 COMPOSANT PRINCIPAL
// --------------------------------------------------------------------------------------------------

const InvestmentCard = ({ investment }) => {
    const navigate = useNavigate();
    
    const [simulations, setSimulations] = useState([]);
    const [timeRemaining, setTimeRemaining] = useState('En attente...');
    const [showChainModal, setShowChainModal] = useState(false); 

    useEffect(() => {
        setSimulations(generateRandomSimulations()); 

        // Logique de simulation du temps (inchangée)
        const minDelay = 1 * 60 * 60 * 1000;
        const maxDelay = 24 * 60 * 60 * 1000;
        const randomDelay = Math.random() * (maxDelay - minDelay) + minDelay;

        const updateTimerDisplay = setInterval(() => {
            if (investment.status === 'completed') { 
                setTimeRemaining('Chaîne complète');
                clearInterval(updateTimerDisplay);
                return;
            }
            
            const timePassed = Date.now() - new Date(investment.createdAt).getTime();
            const remaining = randomDelay - timePassed;
            
            if (remaining > 0) {
                const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
                setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
            } else {
                setTimeRemaining('En attente de complétion...');
            }
        }, 1000);

        return () => clearInterval(updateTimerDisplay);
    }, [investment]);

    const formattedDate = new Date(investment.createdAt).toLocaleDateString();
    
    const planNameEncoded = encodeURIComponent(investment.planName);
    const investmentAmount = parseFloat(investment.amountInOriginalCurrency);
    const fullReferralLink = `${window.location.origin}/investment/${planNameEncoded}/${investmentAmount}?referral=${investment.referralLink}`;
    
    const isCompleted = investment.status === 'completed';
    const directSponsoredCount = investment.sponsoredCount || 0;
    const directProgress = Math.min(Math.round((directSponsoredCount / 2) * 100), 100);

    // 🚨 NOUVEAU : Fonction unique pour la navigation, sans propagation nécessaire ici car le Card est le conteneur.
    const handleViewDetails = () => {
        navigate(`/mes-investissements/${investment._id}`);
    };
    
    // 🚨 MODIFIÉ : Stoppe la propagation car les boutons sont à l'intérieur du Card cliquable.
    const handleCopyClick = (e) => {
        e.stopPropagation(); 
        navigator.clipboard.writeText(fullReferralLink);
        alert('Lien de parrainage copié !');
    };
    
    // 🚨 MODIFIÉ : Stoppe la propagation
    const handleChainModalOpen = (e) => {
        e.stopPropagation(); 
        setShowChainModal(true);
    };

    return (
        <>
            <Card 
                // 🚨 COMPACITÉ : Suppression de my-3 et ajout de p-3 pour un padding réduit à l'extérieur
                className={`investment-card h-100 mb-3 border-0 shadow-sm ${isCompleted ? 'success-card' : ''}`}
                onClick={handleViewDetails} 
                style={{ cursor: 'pointer' }}
            >
                {/* 🚨 COMPACITÉ : Réduction du padding interne à p-3 */}
                <Card.Body className="p-3">
                    
                    {/* LIGNE 1 : Titre et Statut */}
                    <div className="d-flex justify-content-between align-items-center mb-2"> {/* mb-2 au lieu de mb-3 */}
                        {/* 🚨 COMPACITÉ : Titre plus petit et mb-0 */}
                        <Card.Title className="mb-0 fw-bolder fs-6 text-primary">{investment.planName}</Card.Title>
                        {isCompleted ? (
                            <Badge bg="success" className="p-1 fs-7"> {/* p-1 pour un badge plus petit */}
                                <FaCheckCircle className='me-1' /> Terminé
                            </Badge>
                        ) : (
                            <Badge bg="info" className="p-1 fs-7">
                                En cours
                            </Badge>
                        )}
                    </div>
                    
                    {/* LIGNE 2 : Montant & Date */}
                    <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                        <p className="fw-bold mb-0 fs-6">
                            {formatCurrency(investmentAmount)}
                        </p>
                        <small className="text-muted text-end">
                            {formattedDate}
                        </small>
                    </div>
                    
                    {/* SECTION : Simulations (Ultra Compacte) */}
                    <div className="simulations-section mt-2 border-bottom pb-2"> {/* Marge et padding réduits */}
                        <h6 className="text-center mb-1 text-secondary fs-7 fw-bold">Simulation en cours</h6> {/* h6 et fs-7 */}
                        {simulations.map((sim, index) => (
                            // 🚨 COMPACITÉ : mb-0 et font size très réduit
                            <Row key={index} className="mb-0 g-0"> 
                                <Col xs={3} className={`fw-bold fs-7 ${sim.type === 'Achat' ? 'text-success' : sim.type === 'Vente' ? 'text-danger' : 'text-info'}`}>{sim.type}</Col>
                                <Col xs={4} className="fw-bold fs-7">{sim.company}</Col>
                                <Col xs={5} className="text-muted text-end fs-7 text-truncate">{sim.justification}</Col>
                            </Row>
                        ))}
                    </div>
                    
                    {/* SECTION : Progression/Complétée */}
                    {!isCompleted ? (
                        <div className="referral-section mt-3"> {/* Marge réduite à mt-3 */}
                            <h6 className="mb-2 fs-7 fw-bold text-center">Progression de la chaîne</h6> {/* h6 et fs-7 */}
                            
                            {/* Temps restant */}
                            <div className='d-flex justify-content-between mb-2'>
                                <small className='text-muted'><FaClock className='me-1 text-primary' /> Temps estimé restant</small>
                                <small className='fw-bold text-primary'>{timeRemaining}</small>
                            </div>

                            {/* Barre de Progression Directe */}
                            <div className="mb-2">
                                <small className="fw-bold d-block mb-1">
                                    Filleuls directs: {directSponsoredCount} / 2
                                </small>
                                <ProgressBar now={directProgress} label={`${directProgress}%`} variant="info" style={{ height: '10px' }} />
                            </div>

                            {/* Liens et Boutons d'Action (moins de marge) */}
                            <div className="input-group mb-2"> {/* mb-2 */}
                                <input
                                    type="text"
                                    className="form-control form-control-sm" // Taille réduite
                                    value={fullReferralLink}
                                    readOnly
                                />
                                <Button variant="primary" size="sm" onClick={handleCopyClick}> {/* size=sm */}
                                    <FaCopy />
                                </Button>
                            </div>
                            
                            <div className="d-flex justify-content-center gap-2">
                                <Button 
                                    variant="outline-secondary" 
                                    size="sm"
                                    onClick={handleChainModalOpen}
                                    className='w-50' // Utiliser la moitié de la largeur
                                >
                                    <FaCodeBranch className='me-2' /> Arbre
                                </Button>
                                <Button variant="outline-primary" size="sm" onClick={handleViewDetails} className="w-50">
                                    Détails
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center mt-3">
                            <h5 className="text-success fw-bold fs-6">Chaîne Complétée ! 🎉</h5> {/* Titre plus petit */}
                            <p className="text-muted fs-7 mb-0">
                                Le profit double a été crédité à votre solde.
                            </p>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <ReferralChainModal 
                show={showChainModal} 
                handleClose={() => setShowChainModal(false)} 
                investmentId={investment._id} 
            />
        </>
    );
};

export default InvestmentCard;